const BASE_FONT_SIZE = 14
const MIN_FONT_SIZE = 6
const SCALE_START_SLICES = 6
const SCALE_RATE = 0.25
const BASE_CHAR_WIDTH = 9
export const TEXT_MAX_RADIUS = 176
export const TEXT_MIN_RADIUS = 35
const AVAILABLE_SPACE = TEXT_MAX_RADIUS - TEXT_MIN_RADIUS
const MAX_SLICES_FOR_TWO_LINES = 10
const WORD_BREAK_MIN_RATIO = 0.4
const SECOND_LINE_BUDGET_RATIO = 0.85

// Proportional character widths (relative to base width, Poppins Bold approximation)
const CHAR_WIDTHS = {
  " ": 0.45,
  "!": 0.5,
  '"': 0.65,
  "'": 0.45,
  "(": 0.55,
  ")": 0.55,
  ",": 0.45,
  "-": 0.6,
  ".": 0.45,
  "/": 0.65,
  ":": 0.45,
  ";": 0.45,
  1: 0.65,
  I: 0.55,
  "[": 0.55,
  "\\": 0.65,
  "]": 0.55,
  f: 0.65,
  i: 0.5,
  j: 0.55,
  l: 0.5,
  r: 0.65,
  t: 0.65,
  "{": 0.6,
  "|": 0.45,
  "}": 0.6,
  M: 1.3,
  O: 1.1,
  Q: 1.15,
  W: 1.3,
  "@": 1.25,
  m: 1.25,
  w: 1.15,
  "%": 1.1,
}
const DEFAULT_CHAR_WIDTH = 1.05

export const sliceFontSize = (sliceCount) => {
  if (sliceCount <= SCALE_START_SLICES) return BASE_FONT_SIZE
  const scaled = BASE_FONT_SIZE - (sliceCount - SCALE_START_SLICES) * SCALE_RATE
  return Math.max(MIN_FONT_SIZE, Math.round(scaled))
}

export const scaledCharWidth = (fontSize) => BASE_CHAR_WIDTH * (fontSize / BASE_FONT_SIZE)

const charWidth = (char, baseWidth) => (CHAR_WIDTHS[char] ?? DEFAULT_CHAR_WIDTH) * baseWidth

export const measureTextWidth = (text, fontSize) => {
  const baseWidth = scaledCharWidth(fontSize)
  return [...text].reduce((total, char) => total + charWidth(char, baseWidth), 0)
}

// Number of leading characters of text that fit within maxPixels.
const charsFitting = (text, maxPixels, fontSize) => {
  const baseWidth = scaledCharWidth(fontSize)
  let total = 0
  for (let i = 0; i < text.length; i++) {
    total += charWidth(text[i], baseWidth)
    if (total > maxPixels) return i
  }
  return text.length
}

// Two lines only when < 10 slices to prevent overrunning neighbors
export const wrapSliceText = (text, fontSize, sliceCount) => {
  const maxPixels = AVAILABLE_SPACE
  const allowTwoLines = sliceCount < MAX_SLICES_FOR_TWO_LINES

  // If text fits on one line, return single line array
  if (measureTextWidth(text, fontSize) <= maxPixels) {
    return { lines: [text], isTruncated: false }
  }

  // Find how many chars fit in the available space
  const maxChars = charsFitting(text, maxPixels, fontSize)

  // Too many slices for two lines — truncate to one line
  if (!allowTwoLines) {
    return { lines: [text.slice(0, maxChars - 1) + "…"], isTruncated: true }
  }

  // Find a good break point for first line (prefer word boundary)
  let breakIndex = maxChars
  const spaceIndex = text.lastIndexOf(" ", maxChars)
  if (spaceIndex > maxChars * WORD_BREAK_MIN_RATIO) {
    breakIndex = spaceIndex
  }

  const line1 = text.slice(0, breakIndex).trim()
  let line2 = text.slice(breakIndex).trim()
  let isTruncated = false

  // Second line needs fewer pixels to avoid overrunning wheel edge
  const maxPixelsLine2 = maxPixels * SECOND_LINE_BUDGET_RATIO
  if (measureTextWidth(line2, fontSize) > maxPixelsLine2) {
    const maxCharsLine2 = charsFitting(line2, maxPixelsLine2, fontSize)
    line2 = line2.slice(0, maxCharsLine2 - 1) + "…"
    isTruncated = true
  }

  return { lines: [line1, line2], isTruncated }
}
